export const SIGNUP_CONTEXT_COOKIE = 'mynawoo_signup_context';
const INITIAL_CONTEXT_STORAGE_KEY = 'mynawoo_initial_signup_context';

export type SignupIntent = 'learning' | 'connector';

export interface SignupContext {
  signup_intent: SignupIntent;
  initial_path: string;
  referrer: string;
  attribution: Record<string, string>;
  mag_recorded_at?: number;
}

export function intentFromCallback(callbackUrl = ''): SignupIntent {
  return callbackUrl.startsWith('/oauth/') ? 'connector' : 'learning';
}

function readStoredContext(): Partial<SignupContext> {
  if (typeof window === 'undefined') return {};
  try {
    const stored = JSON.parse(localStorage.getItem(INITIAL_CONTEXT_STORAGE_KEY) || '{}');
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};
    const context: Partial<SignupContext> = {
      initial_path: typeof stored.initial_path === 'string' ? stored.initial_path : '',
      referrer: typeof stored.referrer === 'string' ? stored.referrer : '',
      attribution: {},
    };
    if (stored.attribution && typeof stored.attribution === 'object') {
      for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'mag_post_code', 'mag_entry_path']) {
        if (typeof stored.attribution[key] === 'string') context.attribution![key] = stored.attribution[key].slice(0, 255);
      }
    }
    const age = Date.now() - stored.mag_recorded_at;
    if (typeof stored.mag_recorded_at === 'number' && age >= 0 && age < 30 * 86400_000) {
      context.mag_recorded_at = stored.mag_recorded_at;
    } else {
      delete context.attribution!.mag_post_code;
      delete context.attribution!.mag_entry_path;
    }
    return context;
  } catch {
    return {};
  }
}

export function getSignupContext(callbackUrl = ''): SignupContext {
  if (typeof window === 'undefined') {
    return { signup_intent: intentFromCallback(callbackUrl), initial_path: callbackUrl, referrer: '', attribution: {} };
  }
  const params = new URLSearchParams(window.location.search);
  const existing = readStoredContext();
  const attribution = { ...(existing.attribution || {}) };
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((key) => {
    const value = params.get(key);
    if (value && !attribution[key]) attribution[key] = value;
  });
  const context: SignupContext = {
    signup_intent: intentFromCallback(callbackUrl),
    initial_path: existing.initial_path || callbackUrl || `${window.location.pathname}${window.location.search}`,
    referrer: existing.referrer || document.referrer || '',
    attribution,
    mag_recorded_at: existing.mag_recorded_at,
  };
  try {
    localStorage.setItem(INITIAL_CONTEXT_STORAGE_KEY, JSON.stringify(context));
  } catch {
    // Storage can be unavailable in embedded/private browsers. Authentication
    // must still work; return the context collected for this request.
  }
  return context;
}

export function persistSignupContext(callbackUrl = '') {
  const context = getSignupContext(callbackUrl);
  if (typeof document !== 'undefined') {
    document.cookie = `${SIGNUP_CONTEXT_COOKIE}=${encodeURIComponent(JSON.stringify(context))}; path=/; max-age=1800; SameSite=Lax`;
  }
  return context;
}

export function recordMagAttribution(postCode: string) {
  if (typeof window === 'undefined' || !postCode) return;
  const existing = getSignupContext();
  const attribution = { ...(existing.attribution || {}) };
  if (!attribution.mag_post_code) {
    attribution.mag_post_code = postCode.slice(0, 50);
    attribution.mag_entry_path = window.location.pathname.slice(0, 255);
  }
  const context: SignupContext = {
    signup_intent: existing.signup_intent || 'learning',
    initial_path: existing.initial_path || `${window.location.pathname}${window.location.search}`,
    referrer: existing.referrer || document.referrer || '',
    attribution,
    mag_recorded_at: existing.mag_recorded_at || Date.now(),
  };
  try {
    localStorage.setItem(INITIAL_CONTEXT_STORAGE_KEY, JSON.stringify(context));
  } catch {
    // Attribution is best-effort and must not interrupt reading.
  }
}
